import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface MovieQuickViewContextValue {
  quickViewMovieId: string | null;
  openMovieQuickView: (movieId: string) => void;
  /**
   * Explicit close (drag-down, ← button). Pops the history entry pushed when
   * the sheet opened, so the user lands back on the originating page cleanly.
   */
  closeMovieQuickView: () => void;
  /**
   * Called before navigating away FROM the sheet (person page, user profile, full
   * movie page, etc.). Closes the sheet visually but does NOT pop the history
   * entry — so the browser back button can return the user to the sheet.
   */
  hideSheetForNavigation: () => void;
}

const MovieQuickViewContext = createContext<MovieQuickViewContextValue | null>(null);

// History state key. Kept short to avoid polluting URL state.
const PREVIEW_KEY = '_mqv';

export const MovieQuickViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [quickViewMovieId, setQuickViewMovieId] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // True when we have pushed a history entry for the sheet (so we know to pop it on close).
  const sheetEntryPushedRef = useRef(false);

  // ─── Sync sheet state from history ──────────────────────────────────────────
  // Fires on every history navigation (location.key is unique per entry).
  // This is what makes back-navigation reopen the sheet.
  useEffect(() => {
    const previewId = (location.state as Record<string, unknown> | null)?.[PREVIEW_KEY] as string | undefined;
    if (previewId) {
      setQuickViewMovieId(previewId);
      sheetEntryPushedRef.current = true;
    } else {
      // No sheet state in this history entry — close if sheet was open.
      setQuickViewMovieId(null);
      sheetEntryPushedRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // ─── Open ────────────────────────────────────────────────────────────────────
  const openMovieQuickView = useCallback((movieId: string) => {
    // Set immediately for visual responsiveness (effect will also fire once navigate settles).
    setQuickViewMovieId(movieId);

    // If sheet already open (browsing similar movies): replace so we don't build up a long
    // back-stack of individual movie entries. If not open: push so back closes cleanly.
    const alreadyOpen = sheetEntryPushedRef.current;
    sheetEntryPushedRef.current = true;

    navigate(location.pathname + location.search, {
      state: { ...(location.state as object | null), [PREVIEW_KEY]: movieId },
      replace: alreadyOpen,
    });
  }, [location, navigate]);

  // ─── Explicit close (drag-down / ← button) ───────────────────────────────────
  const closeMovieQuickView = useCallback(() => {
    setQuickViewMovieId(null);
    if (sheetEntryPushedRef.current) {
      sheetEntryPushedRef.current = false;
      navigate(-1); // Pop the sheet history entry → land on originating page cleanly
    }
  }, [navigate]);

  // ─── Navigation-away close ───────────────────────────────────────────────────
  // Closes the sheet visually but keeps the sheet history entry in place.
  // When the user presses back from the destination, location.key changes back
  // to the sheet entry → the useEffect above reopens the sheet automatically.
  const hideSheetForNavigation = useCallback(() => {
    setQuickViewMovieId(null);
    // sheetEntryPushedRef stays true so back-nav finds the entry.
  }, []);

  return (
    <MovieQuickViewContext.Provider value={{
      quickViewMovieId,
      openMovieQuickView,
      closeMovieQuickView,
      hideSheetForNavigation,
    }}>
      {children}
    </MovieQuickViewContext.Provider>
  );
};

export const useMovieQuickView = (): MovieQuickViewContextValue => {
  const ctx = useContext(MovieQuickViewContext);
  if (!ctx) throw new Error('useMovieQuickView must be used inside MovieQuickViewProvider');
  return ctx;
};
